import React, { useState } from 'react';
import { cn } from '../lib/utils';
import { SectionHeader } from '../components/ui/SectionHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { CampaignEmailPreviewModal } from '../components/campaigns/CampaignEmailPreviewModal';
import {
  Megaphone,
  Mail,
  MessageSquare,
  Phone,
  CheckCircle2,
  Clock,
  PauseCircle,
  Sparkles,
  Users,
} from 'lucide-react';

// ---- Types ----

type CampaignStatus = 'active' | 'paused' | 'completed' | 'draft' | 'approved';
type CampaignChannel = 'email' | 'sms' | 'phone';

interface Campaign {
  id: string;
  name: string;
  status: CampaignStatus;
  channel: CampaignChannel;
  segmentSize: number;
  sentCount: number;
  approvedVariants: number;
  aiGenerated: boolean;
  createdAt: string;
  emailBody?: string;
}

// ---- Mock data ----

const MOCK_CAMPAIGNS: Campaign[] = [
  {
    id: 'camp-001',
    name: 'Auto Loan Spring Promo',
    status: 'active',
    channel: 'email',
    segmentSize: 2400,
    sentCount: 1820,
    approvedVariants: 3,
    aiGenerated: true,
    createdAt: new Date(Date.now() - 3 * 24 * 3600000).toISOString(),
    emailBody: '<h2>Drive away in your dream car this spring!</h2><p>Hi {{member.firstName}},</p><p>{{cu.name}} is offering special auto loan rates this spring. Apply today for rates as low as 3.9% APR on {{loan.type}} financing up to {{loan.amount}} over {{loan.term}}.</p><p>Questions? Call us at {{cu.phone}}.</p>',
  },
  {
    id: 'camp-002',
    name: 'CD Maturity Outreach',
    status: 'completed',
    channel: 'sms',
    segmentSize: 640,
    sentCount: 640,
    approvedVariants: 2,
    aiGenerated: true,
    createdAt: new Date(Date.now() - 14 * 24 * 3600000).toISOString(),
  },
  {
    id: 'camp-003',
    name: 'HELOC Awareness Q2',
    status: 'paused',
    channel: 'email',
    segmentSize: 1100,
    sentCount: 310,
    approvedVariants: 2,
    aiGenerated: false,
    createdAt: new Date(Date.now() - 7 * 24 * 3600000).toISOString(),
    emailBody: '<h2>Unlock the equity in your home</h2><p>Hi {{member.firstName}},</p><p>Your home may be worth more than you think. {{cu.name}} offers competitive HELOC rates starting at 6.25% APR. Use your equity for home improvements, education, or anything life brings.</p><p>Contact us at {{cu.phone}} to learn more.</p>',
  },
  {
    id: 'camp-004',
    name: 'Member Wellness Check-In',
    status: 'draft',
    channel: 'phone',
    segmentSize: 450,
    sentCount: 0,
    approvedVariants: 0,
    aiGenerated: true,
    createdAt: new Date(Date.now() - 1 * 24 * 3600000).toISOString(),
  },
];

// ---- Helpers ----

function fmtRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ---- Status config ----

interface StatusMeta {
  label: string;
  cls: string;
  Icon: typeof Clock;
}

const STATUS_MAP: Record<CampaignStatus, StatusMeta> = {
  active: { label: 'Active', cls: 'bg-pass-soft text-pass', Icon: CheckCircle2 },
  paused: { label: 'Paused', cls: 'bg-warn-soft text-warn', Icon: PauseCircle },
  completed: { label: 'Completed', cls: 'bg-brand-tint text-brand-dark', Icon: CheckCircle2 },
  draft: { label: 'Draft', cls: 'bg-sunken text-ink-soft', Icon: Clock },
  approved: { label: 'Approved', cls: 'bg-pass-soft text-pass', Icon: CheckCircle2 },
};

// ---- Channel config ----

interface ChannelMeta {
  label: string;
  Icon: typeof Mail;
}

const CHANNEL_MAP: Record<CampaignChannel, ChannelMeta> = {
  email: { label: 'Email', Icon: Mail },
  sms: { label: 'SMS', Icon: MessageSquare },
  phone: { label: 'Phone', Icon: Phone },
};

// ---- CampaignCard ----

interface CampaignCardProps {
  campaign: Campaign;
  onApprove: () => void;
}

function CampaignCard({ campaign, onApprove }: CampaignCardProps): React.ReactElement {
  const [previewOpen, setPreviewOpen] = useState(false);
  const statusMeta = STATUS_MAP[campaign.status];
  const channelMeta = CHANNEL_MAP[campaign.channel];
  const sentPct = campaign.segmentSize > 0
    ? Math.round((campaign.sentCount / campaign.segmentSize) * 100)
    : 0;

  return (
    <>
      <div className="bg-white rounded-xl border border-sunken shadow-sm p-5">
        <div className="flex flex-wrap items-start gap-2 mb-3">
          {/* Campaign name */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-running-soft flex items-center justify-center flex-shrink-0">
              <Megaphone className="h-4 w-4 text-running" aria-hidden />
            </div>
            <span className="font-medium text-ink truncate">{campaign.name}</span>
          </div>

          {/* Status badge */}
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
              statusMeta.cls,
            )}
          >
            <statusMeta.Icon className="h-3 w-3" aria-hidden />
            {statusMeta.label}
          </span>

          {/* Channel badge */}
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-bg text-ink-soft">
            <channelMeta.Icon className="h-3 w-3" aria-hidden />
            {channelMeta.label}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-4 text-sm">
          {/* Segment size */}
          <span className="flex items-center gap-1.5 text-ink-soft">
            <Users className="h-4 w-4 text-ink-muted" aria-hidden />
            <span className="font-medium text-ink">{campaign.segmentSize.toLocaleString()}</span>
            <span className="text-ink-muted">members</span>
          </span>

          {/* Sent progress */}
          {campaign.status !== 'draft' && (
            <span className="flex items-center gap-1.5 text-ink-soft">
              <span className="font-medium text-ink">{campaign.sentCount.toLocaleString()}</span>
              <span className="text-ink-muted">/ {campaign.segmentSize.toLocaleString()} sent</span>
              <span
                className={cn(
                  'text-xs font-medium',
                  sentPct === 100 ? 'text-pass' : 'text-warn',
                )}
              >
                ({sentPct}%)
              </span>
            </span>
          )}

          {/* AI variants badge */}
          {campaign.aiGenerated && (
            <span
              className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                'bg-running-soft text-running-ink',
              )}
            >
              <Sparkles className="h-3 w-3" aria-hidden />
              AI Variants
            </span>
          )}

          {/* Preview Email button — email channel only */}
          {campaign.channel === 'email' && (
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-pass hover:text-pass transition-colors"
            >
              <Mail className="h-3.5 w-3.5" aria-hidden />
              Preview Email
            </button>
          )}

          {/* Created at — pushed right when no preview button */}
          {campaign.channel !== 'email' && (
            <span className="ml-auto text-xs text-ink-muted">{fmtRelative(campaign.createdAt)}</span>
          )}
        </div>
      </div>

      {/* Email preview modal */}
      <CampaignEmailPreviewModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        campaignName={campaign.name}
        emailBody={campaign.emailBody ?? ''}
        onApprove={() => { onApprove(); setPreviewOpen(false); }}
        campaignStatus={campaign.status}
      />
    </>
  );
}

// ---- CampaignsPage ----

export function CampaignsPage(): React.ReactElement {
  const [_filter, setFilter] = useState<CampaignStatus | 'all'>('all');
  const [showEmpty, setShowEmpty] = useState(false);
  const [campaigns, setCampaigns] = useState<Campaign[]>(MOCK_CAMPAIGNS);

  const isEmpty = showEmpty || campaigns.length === 0;

  function handleApprove(campaignId: string): void {
    setCampaigns((prev) =>
      prev.map((c) => (c.id === campaignId ? { ...c, status: 'approved' as CampaignStatus } : c)),
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <SectionHeader title="Campaigns" />
          <button
            type="button"
            onClick={() => setShowEmpty(!showEmpty)}
            className="text-xs text-ink-muted hover:text-ink-soft ml-2"
          >
            [Toggle empty]
          </button>
        </div>
        <p className="text-sm text-ink-muted mt-1">
          AI-generated campaign messages, segment builder, consent-gated sends
        </p>
      </div>

      {/* Campaign cards or empty state */}
      {isEmpty ? (
        <EmptyState
          icon={Megaphone}
          headline="No active campaigns"
          body="Create AI-generated campaign messages with consent-gated delivery to member segments."
          ctaLabel="Create Campaign"
          onCta={() => {}}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {campaigns.map((campaign) => (
            <CampaignCard key={campaign.id} campaign={campaign} onApprove={() => handleApprove(campaign.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
